import dynamic from 'next/dynamic';
import type { ComponentProps } from './tsHelpers';

// sections
import { Hero } from '@/components/home/Hero';
import { CustomersReviews } from '@/components/shared';
const HowItWorks = dynamic(() => import('@/components/shared').then(mod => mod.HowItWorks));
const Prices = dynamic(() => import('@/components/shared').then(mod => mod.Prices));
const InstantEverything = dynamic(() => import('@/components/shared').then(mod => mod.InstantEverything));
const Trust = dynamic(() => import('@/components/shared').then(mod => mod.Trust));
const Switching = dynamic(() => import('@/components/shared').then(mod => mod.Switching));
const Products = dynamic(() => import('@/components/shared').then(mod => mod.Products));
const GenericProducts = dynamic(() => import('@/components/shared').then(mod => mod.GenericProducts));
const SwitchingEurope = dynamic(() => import('@/components/home/SwitchingEurope').then(mod => mod.SwitchingEurope));
const SuperCharge = dynamic(() => import('@/components/home/SuperCharge/SuperCharge').then(mod => mod.SuperCharge));
import { Hero as RentersHero } from '@/components/renters/Hero';
const RentersGetCovered = dynamic(() => import('@/components/renters/GetCovered').then(mod => mod.GetCovered));
const RentersProtected = dynamic(() => import('@/components/renters/Protected'));
const RentersClaims = dynamic(() => import('@/components/renters/Claims').then(mod => mod.Claims));
const RentersBottomCTA = dynamic(() => import('@/components/renters/BottomCTA').then(mod => mod.BottomCTA));
const RentersFaq = dynamic(() => import('@/components/renters/Faq').then(mod => mod.Faq));
const RentersBottomLogos = dynamic(() => import('@/components/renters/BottomLogos'));
import { Hero as PetHero } from '@/components/pet/Hero';
const PetAppSection = dynamic(() => import('@/components/pet/AppsSection'));
const PetReviews = dynamic(() => import('@/components/pet/Reviews').then(mod => mod.Reviews));
const PetCoverage = dynamic(() => import('@/components/pet/Coverage/Coverage').then(mod => mod.Coverage));
const PetSave = dynamic(() => import('@/components/pet/PetSaveSection/PetSaveSection').then(mod => mod.default));
const PetGoodToKnowSection = dynamic(() =>
  import('@/components/pet/GoodToKnowSection/GoodToKnowSection').then(mod => mod.default),
);
const PetDoggy = dynamic(() => import('@/components/pet/Doggy').then(mod => mod.Doggy));
const PetGoodSection = dynamic(() => import('@/components/pet/PetGoodSection').then(mod => mod.PetGoodSection));

import { Hero as HomeownersHero } from '@/components/homeowners/Hero';
const HomeownersTrusted = dynamic(() => import('@/components/homeowners/Trusted').then(mod => mod.Trusted));
const HomeownersClosing = dynamic(() => import('@/components/homeowners/Closing').then(mod => mod.Closing));
const HomeownersSwitching = dynamic(() => import('@/components/homeowners/Switching'));
const HomeownersVideos = dynamic(() => import('@/components/homeowners/videos/Videos').then(mod => mod.Videos));
const HomeownersFaq = dynamic(() => import('@/components/homeowners/faq/Faq').then(mod => mod.Faq));
const HomeownersCoverage = dynamic(() => import('@/components/homeowners/coverage/Coverage').then(mod => mod.Coverage));
const HomeownersBottomCTA = dynamic(() => import('@/components/homeowners/BottomCTA').then(mod => mod.BottomCTA));
const HomeownersBottomLogos = dynamic(() => import('@/components/renters/BottomLogos'));
import { Hero as LifeHero } from '@/components/life/Hero';
const LifeTrusted = dynamic(() => import('@/components/life/Trusted'));
const LifeCoverages = dynamic(() => import('@/components/life/Coverages'));
const LifeKnowledge = dynamic(() => import('@/components/life/Knowledge'), {
  ssr: false,
});
import CarHero from '@/components/car/Driving';
const CarOverallCoverages = dynamic(() => import('@/components/car/OverallCoverages'));
const CarFaq = dynamic(() => import('@/components/car/Faq').then(mod => mod.Faq));
const CarBasicCoverages = dynamic(() => import('@/components/car/BasicCoverages'));
const CarForest = dynamic(() => import('@/components/car/Forest'), {
  ssr: false,
});
const CarBottomCta = dynamic(() => import('@/components/car/Footer'));
const CarClaims = dynamic(() => import('@/components/car/Claims'));
const CarBundles = dynamic(() => import('@/components/car/Bundles'));
const CarLowMilage = dynamic(() => import('@/components/car/LowMilage'));
const CarLowMilagePpm = dynamic(() => import('@/components/car/LowMilagePpm'));
const CarElectricCoverages = dynamic(() => import('@/components/car/ElectricCoverages'));
const BuildByYou = dynamic(() => import('@/components/shared/BuildByYou/BuildByYou').then(mod => mod.BuildByYou));
const GeneralCatty = dynamic(() => import('@/components/shared/General/GeneralCatty').then(mod => mod.GeneralCatty));
const BundlesAdapter = dynamic(() =>
  import('@/components/lpg/components/BundlesAdapter').then(mod => mod.BundlesAdapter),
);
import { Hero as LpgHero } from '@/components/lpg/components/Hero';

// Extend LPG config: add more product and config maps by locale here
export const COMPONENTS_MAP = {
  // ---------- home page
  hero: Hero,
  reviews: CustomersReviews,
  products: Products,
  instant: InstantEverything,
  switching: Switching,
  how_it_works: HowItWorks,
  trust: Trust,
  pricing: Prices,
  eu_products: GenericProducts,
  eu_switching: SwitchingEurope,
  supercharge: SuperCharge,
  // ---------- pet
  pet_faq: PetGoodToKnowSection,
  pet_hero: PetHero,
  pet_reviews: PetReviews,
  pet_apps: PetAppSection,
  pet_coverage: PetCoverage,
  pet_save: PetSave,
  pet_good: PetGoodSection,
  pet_doggy: PetDoggy,
  pet_good_to_know: PetGoodToKnowSection,
  // ---------- homeowners
  ho_hero: HomeownersHero,
  ho_reviews: CustomersReviews,
  ho_apps: HomeownersClosing,
  ho_trusted: HomeownersTrusted,
  ho_switching: HomeownersSwitching,
  ho_videos: HomeownersVideos,
  ho_coverage: HomeownersCoverage,
  ho_faqs: HomeownersFaq,
  ho_bottom_cta: HomeownersBottomCTA,
  ho_giving_back: HomeownersBottomLogos,
  ho_products: Products,
  // ---------- renters
  renters_hero: RentersHero,
  renters_reviews: CustomersReviews,
  renters_apps: RentersClaims,
  renters_protected: RentersProtected,
  renters_logos: RentersBottomLogos,
  renters_coverage: RentersGetCovered,
  renters_bottom_cta: RentersBottomCTA,
  renters_products: Products,
  renters_faq: RentersFaq,
  // ---------- life
  life_hero: LifeHero,
  life_reviews: LifeTrusted,
  life_flow_steps: LifeCoverages,
  life_knowledge: LifeKnowledge,
  // ---------- car
  car_hero: CarHero,
  car_overall_coverages: CarOverallCoverages,
  car_basic_coverages: CarBasicCoverages,
  car_low_mileage: CarLowMilage,
  car_electric_coverages: CarElectricCoverages,
  car_forest: CarForest,
  car_faq: CarFaq,
  car_bundles: CarBundles,
  car_claims: CarClaims,
  car_reviews: CustomersReviews,
  car_bottom_cta: CarBottomCta,
  car_low_mileage_ppm: CarLowMilagePpm,
  // ---------- generic sections
  hero_with_custom_bg: LpgHero,
  hero_with_custom_bg_no_default: LpgHero,
  bundles: BundlesAdapter,
  general_catty: GeneralCatty,
  multi_product_selection: BuildByYou,
} as const;

export const PRODUCT_BY_HERO_SECTION = {
  pet_hero: 'pet',
  life_hero: 'life',
  renters_hero: 'renters',
  ho_hero: 'home', // this is homeowners
  car_hero: 'car',
} as const;

export type HeroSectionsByProduct = keyof typeof PRODUCT_BY_HERO_SECTION;
export type ProductsByHeroSection = (typeof PRODUCT_BY_HERO_SECTION)[keyof typeof PRODUCT_BY_HERO_SECTION];
export type Components = (typeof COMPONENTS_MAP)[keyof typeof COMPONENTS_MAP];
export type StaticComponentProps = ComponentProps<Components>;
export type SectionsType = keyof typeof COMPONENTS_MAP;
